import React from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

export const PageIntro = ({ actions, children, className, eyebrow, title }) => (
    <header className={joinClasses('ui-page-intro', className)}>
        <div className='ui-page-intro-copy'>
            {eyebrow && <span className='ui-eyebrow'>{eyebrow}</span>}
            <h2 className='ui-page-title'>{title}</h2>
            {children && <p className='ui-page-lead'>{children}</p>}
        </div>
        {actions && <div className='ui-page-intro-actions'>{actions}</div>}
    </header>
);

export const Panel = ({ actions, as: Component = 'section', children, className, eyebrow, icon: Icon, title, tone = 'white' }) => (
    <Component className={joinClasses('ui-panel', `is-${tone}`, className)}>
        {(title || Icon || actions) && (
            <div className='ui-panel-heading'>
                <SectionHeading eyebrow={eyebrow} icon={Icon} title={title} />
                {actions && <div className='ui-panel-actions'>{actions}</div>}
            </div>
        )}
        {children}
    </Component>
);

export const SectionHeading = ({ className, eyebrow, icon: Icon, title }) => (
    <div className={joinClasses('ui-section-heading', className)}>
        {Icon && <IconTile icon={Icon} />}
        <div className='ui-section-heading-copy'>
            {eyebrow && <span className='ui-eyebrow'>{eyebrow}</span>}
            {title && <strong className='ui-section-title'>{title}</strong>}
        </div>
    </div>
);

export const IconTile = ({ className, icon: Icon, tone = 'amber' }) => (
    <span className={joinClasses('ui-icon-tile', `is-${tone}`, className)}>
        <Icon fontSize='small' />
    </span>
);

export const KpiGrid = ({ children, className, columns }) => (
    <div className={joinClasses('ui-kpi-grid', className)} style={columns ? { '--ui-kpi-columns': columns } : undefined}>
        {children}
    </div>
);

export const KpiCard = ({ as: Component = 'article', className, helper, icon: Icon, label, tone = 'amber', value, variation }) => (
    <Component className={joinClasses('ui-kpi', `is-${tone}`, className)}>
        <div className='ui-kpi-head'>
            <span className='ui-eyebrow'>{label}</span>
            {Icon && <IconTile icon={Icon} tone={tone} />}
        </div>
        <strong className='ui-kpi-value' title={typeof value === 'string' ? value : undefined}>{value}</strong>
        {(variation || helper) && <small className='ui-kpi-foot'>{variation || helper}</small>}
    </Component>
);

export const AttentionBox = ({ children, className, icon: Icon, title, tone = 'amber' }) => (
    <div className={joinClasses('ui-attention', `is-${tone}`, className)}>
        {Icon && <Icon fontSize='small' />}
        <div className='ui-attention-copy'>
            {title && <strong>{title}</strong>}
            {typeof children === 'string' ? <p>{children}</p> : children}
        </div>
    </div>
);

export const TwoColumnLayout = ({ aside, as: Component = 'div', children, className, ...props }) => (
    <Component className={joinClasses('ui-two-column', className)} {...props}>
        <div className='ui-two-column-main'>{children}</div>
        <aside className='ui-two-column-aside'>{aside}</aside>
    </Component>
);

export const SummaryList = ({ children, className }) => (
    <dl className={joinClasses('ui-summary-list', className)}>{children}</dl>
);

export const SummaryItem = ({ empty = false, label, value }) => (
    <div className='ui-summary-item'>
        <dt>{label}</dt>
        <dd className={empty ? 'is-empty' : undefined}>{value}</dd>
    </div>
);

export const FieldRow = ({ children, className, icon: Icon }) => (
    <div className={joinClasses('ui-field-row', className)}>
        {Icon && <Icon fontSize='small' />}
        <div className='ui-field-row-control'>{children}</div>
    </div>
);

export const SegmentedControl = ({ ariaLabel, className, onChange, options, value }) => (
    <div className={joinClasses('ui-segmented', className)} role='radiogroup' aria-label={ariaLabel}>
        {options.map((option) => {
            const Icon = option.icon;
            const active = option.value === value;
            return (
                <button
                    key={option.value}
                    type='button'
                    role='radio'
                    aria-checked={active}
                    className={joinClasses('ui-segmented-option', active && 'is-active')}
                    onClick={() => onChange(option.value)}
                >
                    {Icon && <Icon fontSize='small' />}
                    <span>{option.label}</span>
                </button>
            );
        })}
    </div>
);

export const ModePanel = ({ children, eyebrow = 'Modo de lançamento', icon, title }) => (
    <div className='ui-panel ui-mode-panel'>
        <SectionHeading eyebrow={eyebrow} icon={icon} title={title} />
        {children}
    </div>
);

export const ChoiceChips = ({ ariaLabel, className, onToggle, options, values }) => (
    <div className={joinClasses('ui-choice-chips', className)} role='group' aria-label={ariaLabel}>
        {options.map((option) => {
            const active = Boolean(values[option.value]);
            return (
                <button
                    key={option.value}
                    type='button'
                    aria-pressed={active}
                    className={joinClasses('ui-choice-chip', active && 'is-active')}
                    onClick={() => onToggle(option.value, !active)}
                >
                    {active ? <CheckRoundedIcon fontSize='inherit' /> : <AddRoundedIcon fontSize='inherit' />}
                    <span>{option.label}</span>
                </button>
            );
        })}
    </div>
);
